/**
 * StatementManagementConsole — Package 5 UI.
 *
 * Deterministic operational statement engine. Every monetary figure
 * displayed here is reconstructed from the immutable operational events
 * and commercial versions that produced it — nothing is derived from
 * mutable UI state.
 *
 * Statements-only; no payment execution. Settlement adapters are provider
 * neutral. No external providers are contacted.
 *
 * Route: /engage/statements
 * API base: /api/engage/v2/statements
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/engage/v2/statements`;
const WRITE_ROLES = new Set([
  "platform_owner", "platform_administrator",
  "owner", "director", "admin", "manager", "regional_manager",
  "commercial_manager",
]);
const FREQUENCIES = ["weekly", "fortnightly", "monthly", "quarterly", "custom"];
const GROUPINGS = ["organisation", "campaign", "fundraiser"];
const ADJUSTMENT_KINDS = [
  "credit_adjustment", "debit_adjustment",
  "correction_statement", "replacement_statement",
];

const auth = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("taptrack_token") || ""}` },
});
const humanise = (e) =>
  e?.response?.data?.detail?.message ||
  e?.response?.data?.detail?.code ||
  e?.response?.data?.message ||
  e?.message || "Something went wrong.";

const money = (pence) => {
  if (pence == null) return "—";
  const s = (pence / 100).toFixed(2);
  return `£${s}`;
};

function LifecycleBadge({ value }) {
  const map = {
    draft: "outline",
    calculated: "secondary",
    awaiting_approval: "secondary",
    approved: "default",
    issued: "default",
    frozen: "default",
    settled: "default",
    superseded: "destructive",
  };
  return (
    <Badge variant={map[value] || "outline"} data-testid={`lifecycle-${value}`}>
      {value}
    </Badge>
  );
}

// ─── Overview / dashboard ────────────────────────────────────────────────
function OverviewTab({ health }) {
  return (
    <Card data-testid="statements-overview">
      <CardHeader>
        <CardTitle>Statement Engine Overview</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <span className="text-muted-foreground">Calculation engine:</span>{" "}
          <span className="font-mono" data-testid="overview-engine">
            {health?.calculationEngine || "—"}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Financial execution:</span>{" "}
          <Badge variant="destructive" data-testid="overview-financial-exec">disabled</Badge>
        </div>
        <div>
          <span className="text-muted-foreground">Active settlement port:</span>{" "}
          <span className="font-mono">
            {health?.settlement?.active?.provider || "—"}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Future adapters:</span>{" "}
          {Object.keys(health?.settlement?.futureAdapters || {}).join(", ") || "—"}
        </div>
        <div className="pt-2 text-muted-foreground">
          Every monetary figure on issued statements is reproducible from the
          immutable operational events and the commercial versions that
          governed the activity at the time it occurred.
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Generate dialog ─────────────────────────────────────────────────────
function GenerateDialog({ open, onClose, onGenerated, canWrite }) {
  const [form, setForm] = useState({
    periodStart: "2020-01-01T00:00:00Z",
    periodEnd: "2030-01-01T00:00:00Z",
    frequency: "monthly",
    grouping: "organisation",
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/preview`, form, auth());
      setPreview(r.data);
    } catch (e) { toast.error(humanise(e)); }
    finally { setLoading(false); }
  };
  const runGenerate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/generate`, form, auth());
      toast.success(`Statement ${r.data.statement.reference} generated`);
      onGenerated?.(r.data.statement.id);
      onClose();
    } catch (e) { toast.error(humanise(e)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="generate-dialog">
        <DialogHeader>
          <DialogTitle>Generate Statement</DialogTitle>
          <DialogDescription>
            Preview or commit a deterministic statement calculation. Preview
            never persists or issues anything.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Period start (ISO)</Label>
            <Input value={form.periodStart}
                   onChange={(e) => upd("periodStart", e.target.value)}
                   data-testid="generate-period-start" />
          </div>
          <div>
            <Label>Period end (ISO)</Label>
            <Input value={form.periodEnd}
                   onChange={(e) => upd("periodEnd", e.target.value)}
                   data-testid="generate-period-end" />
          </div>
          <div>
            <Label>Frequency</Label>
            <Select value={form.frequency} onValueChange={(v) => upd("frequency", v)}>
              <SelectTrigger data-testid="generate-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FREQUENCIES.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`generate-frequency-${f}`}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Grouping</Label>
            <Select value={form.grouping} onValueChange={(v) => upd("grouping", v)}>
              <SelectTrigger data-testid="generate-grouping"><SelectValue /></SelectTrigger>
              <SelectContent>
                {GROUPINGS.map((g) => (
                  <SelectItem key={g} value={g} data-testid={`generate-grouping-${g}`}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {preview && (
          <div className="border-t pt-4" data-testid="generate-preview-block">
            <div className="text-sm">
              <b>Preview only — no statement issued.</b>{" "}
              Lines: {preview.lines?.length || 0} · Net: {money(preview.subtotalPence)}{" "}
              · VAT: {money(preview.vatPence)} · Total: {money(preview.totalPence)}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={runPreview} disabled={loading}
                    data-testid="generate-preview-btn">Preview</Button>
          <Button onClick={runGenerate} disabled={loading || !canWrite}
                    data-testid="generate-commit-btn">Generate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Statement list ─────────────────────────────────────────────────────
function StatementList({ lifecycleState, onOpen, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try {
      const params = lifecycleState ? { lifecycleState } : {};
      const r = await axios.get(API, { ...auth(), params });
      setRows(r.data.statements || []);
    } catch (e) { setErr(humanise(e)); }
  }, [lifecycleState]);
  useEffect(() => { load(); }, [load, refreshKey]);
  return (
    <div data-testid={`stmt-list-${lifecycleState || "all"}`}>
      {err && <div className="text-red-600 text-sm">{err}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Reference</TableHead>
            <TableHead>Period</TableHead>
            <TableHead>Frequency</TableHead>
            <TableHead>State</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">
              No statements found.
            </TableCell></TableRow>
          )}
          {rows.map((s) => (
            <TableRow key={s.id} data-testid={`stmt-row-${s.id}`}>
              <TableCell className="font-mono text-xs">{s.reference}</TableCell>
              <TableCell className="text-xs">
                {(s.periodStart || "").slice(0, 10)} → {(s.periodEnd || "").slice(0, 10)}
              </TableCell>
              <TableCell>{s.frequency}</TableCell>
              <TableCell><LifecycleBadge value={s.lifecycleState} /></TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline"
                          onClick={() => onOpen(s.id)}
                          data-testid={`stmt-open-${s.id}`}>Open</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Detail view + lifecycle actions ────────────────────────────────────
function StatementDetail({ sid, onClose, canWrite, onRefresh }) {
  const [data, setData] = useState(null);
  const [explain, setExplain] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);
  const [lineDrawer, setLineDrawer] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!sid) return;
    try {
      const r = await axios.get(`${API}/${sid}`, auth());
      setData(r.data);
    } catch (e) { setErr(humanise(e)); }
  }, [sid]);
  useEffect(() => { load(); }, [load]);

  const stable = data?.statement;
  const version = data?.currentVersion;

  const act = async (path, body = null, method = "post") => {
    try {
      const r = method === "post"
        ? await axios.post(`${API}/${sid}${path}`, body || {}, auth())
        : await axios.get(`${API}/${sid}${path}`, auth());
      toast.success(`${path.slice(1)} ok`);
      await load();
      onRefresh?.();
      return r.data;
    } catch (e) { toast.error(humanise(e)); return null; }
  };

  const loadExplain = async () => {
    setExplain(await act("/explain", null, "get"));
  };
  const loadEvidence = async () => {
    setEvidence(await act("/evidence", null, "get"));
  };
  const loadLine = async (lid) => {
    try {
      const r = await axios.get(`${API}/${sid}/lines/${lid}/explain`, auth());
      setLineDrawer(r.data);
    } catch (e) { toast.error(humanise(e)); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/${sid}/export.csv`, {
        ...auth(), responseType: "text",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${version?.reference || sid}.csv`;
      a.click();
    } catch (e) { toast.error(humanise(e)); }
  };
  const exportBundle = async () => {
    try {
      const r = await axios.get(`${API}/${sid}/export.bundle`, auth());
      const blob = new Blob([JSON.stringify(r.data, null, 2)],
                              { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${version?.reference || sid}-evidence.json`;
      a.click();
    } catch (e) { toast.error(humanise(e)); }
  };

  if (err) return (
    <Card><CardContent className="text-red-600 py-6" data-testid="stmt-detail-error">{err}</CardContent></Card>
  );
  if (!data) return (
    <Card><CardContent className="py-6" data-testid="stmt-detail-loading">Loading…</CardContent></Card>
  );

  return (
    <Card data-testid={`stmt-detail-${sid}`}>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="font-mono text-lg" data-testid="stmt-detail-reference">
            {version.reference}
          </CardTitle>
          <div className="text-xs text-muted-foreground mt-1">
            Period {(version.periodStart || "").slice(0, 10)} → {(version.periodEnd || "").slice(0, 10)}
            {" · "}Frequency {version.frequency}
            {" · "}<LifecycleBadge value={stable.lifecycleState} />
            {version.contentHash && (
              <span className="ml-2 font-mono">
                hash {version.contentHash.slice(0, 12)}…
              </span>
            )}
          </div>
        </div>
        <Button variant="ghost" onClick={onClose} data-testid="stmt-detail-close">Close</Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Totals */}
        <div className="grid grid-cols-4 gap-4">
          <div><Label>Net</Label><div className="text-lg" data-testid="stmt-net">{money(version.subtotalPence)}</div></div>
          <div><Label>VAT</Label><div className="text-lg">{money(version.vatPence)}</div></div>
          <div><Label>Adjustments</Label><div className="text-lg">{money(version.adjustmentsPence)}</div></div>
          <div><Label>Total</Label><div className="text-lg font-semibold" data-testid="stmt-total">{money(version.totalPence)}</div></div>
        </div>

        {/* Lifecycle actions */}
        <div className="flex flex-wrap gap-2">
          {stable.lifecycleState === "calculated" && (
            <Button size="sm" onClick={() => act("/submit")} disabled={!canWrite}
                      data-testid="stmt-action-submit">Submit for approval</Button>
          )}
          {stable.lifecycleState === "awaiting_approval" && (
            <>
              <Button size="sm" onClick={() => act("/approve", { decision: "approve", note: "ok" })}
                        disabled={!canWrite} data-testid="stmt-action-approve">Approve</Button>
              <Button size="sm" variant="destructive"
                        onClick={() => {
                          const note = window.prompt("Reason for rejection?");
                          if (note && note.trim()) act("/approve", { decision: "reject", note });
                        }}
                        disabled={!canWrite} data-testid="stmt-action-reject">Reject</Button>
            </>
          )}
          {stable.lifecycleState === "approved" && (
            <Button size="sm" onClick={() => act("/issue", {})} disabled={!canWrite}
                      data-testid="stmt-action-issue">Issue</Button>
          )}
          {stable.lifecycleState === "issued" && (
            <Button size="sm" onClick={() => act("/freeze")} disabled={!canWrite}
                      data-testid="stmt-action-freeze">Freeze</Button>
          )}
          <Button size="sm" variant="outline" onClick={loadExplain} data-testid="stmt-action-explain">
            Explain statement
          </Button>
          <Button size="sm" variant="outline" onClick={loadEvidence} data-testid="stmt-action-evidence">
            Evidence explorer
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAdjOpen(true)}
                    disabled={!canWrite} data-testid="stmt-action-adjust">Adjust</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="stmt-action-export-csv">
            Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={exportBundle} data-testid="stmt-action-export-bundle">
            Export evidence bundle
          </Button>
          <Button size="sm" variant="outline"
                    onClick={() => act("/reconciliation", {})} disabled={!canWrite}
                    data-testid="stmt-action-reconcile">Reconcile</Button>
        </div>

        {/* Line items */}
        <div>
          <div className="font-medium mb-1">Line items</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="text-right">VAT</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead>Provenance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(version.lines || []).map((l) => (
                <TableRow key={l.id} data-testid={`stmt-line-${l.id}`}>
                  <TableCell className="text-xs">{l.description}</TableCell>
                  <TableCell className="text-right">{l.quantity}</TableCell>
                  <TableCell className="text-right">{money(l.ratePence)}</TableCell>
                  <TableCell className="text-right">{money(l.netPence)}</TableCell>
                  <TableCell className="text-right">{money(l.vatPence)}</TableCell>
                  <TableCell className="text-right">{money(l.grossPence)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => loadLine(l.id)}
                              data-testid={`stmt-line-explain-${l.id}`}>
                      Explain
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Explain drawer */}
        {explain && (
          <Card data-testid="stmt-explain-panel">
            <CardHeader><CardTitle className="text-base">Statement explanation</CardTitle></CardHeader>
            <CardContent className="text-xs space-y-2">
              <div>{explain.explanation}</div>
              <div className="font-mono">
                Engine: {explain.calculationEngine} v{explain.calculationVersion}
              </div>
              <div>
                {explain.reasons?.map((r, i) => (
                  <div key={i}>
                    <b>{r.factor}:</b> {Array.isArray(r.ids) ? r.ids.join(", ") : (r.id || "—")}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evidence explorer */}
        {evidence && (
          <Card data-testid="stmt-evidence-panel">
            <CardHeader><CardTitle className="text-base">Evidence explorer</CardTitle></CardHeader>
            <CardContent className="text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-medium mb-1">Operational</div>
                  <div>Submissions: {evidence.operational?.submissions?.length || 0}</div>
                  <div>Validations: {evidence.operational?.validations?.length || 0}</div>
                  <div>Communications: {evidence.operational?.communications?.length || 0}</div>
                  <div className="text-muted-foreground mt-1">
                    Unavailable refs are surfaced truthfully — never hidden.
                  </div>
                </div>
                <div>
                  <div className="font-medium mb-1">Commercial versions</div>
                  <div>Pricing versions: {evidence.commercial?.pricingRuleVersions?.length || 0}</div>
                  <div>Contract versions: {evidence.commercial?.contractVersions?.length || 0}</div>
                  <div>Campaign versions: {evidence.commercial?.campaignVersions?.length || 0}</div>
                  <div>Statement-rule versions: {evidence.commercial?.statementRuleVersions?.length || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Adjustment dialog */}
        <AdjustmentDialog
          open={adjOpen} onClose={() => setAdjOpen(false)}
          onDone={() => { setAdjOpen(false); load(); onRefresh?.(); }}
          sid={sid} />

        {/* Line evidence drawer */}
        {lineDrawer && (
          <Dialog open={!!lineDrawer} onOpenChange={() => setLineDrawer(null)}>
            <DialogContent className="max-w-3xl" data-testid="line-drawer">
              <DialogHeader>
                <DialogTitle>Line evidence — {lineDrawer.lineId}</DialogTitle>
                <DialogDescription>{lineDrawer.explanation}</DialogDescription>
              </DialogHeader>
              <div className="text-xs space-y-2">
                <div><b>Commercial references</b></div>
                {Object.entries(lineDrawer.commercialReferences || {}).map(([k, v]) => (
                  <div key={k}><span className="text-muted-foreground">{k}:</span>{" "}
                    <span className="font-mono">{v || "—"}</span></div>
                ))}
                <div className="mt-2"><b>Operational evidence</b></div>
                {Object.entries(lineDrawer.operationalEvidence || {}).map(([k, arr]) => (
                  <div key={k}>
                    <span className="text-muted-foreground">{k}:</span>{" "}
                    <Badge variant="outline">{arr.length}</Badge>
                    {arr.slice(0, 3).map((r, i) => (
                      <span key={i} className="ml-2 font-mono">
                        {r.id?.slice(0, 12)}
                        <Badge variant={r.state === "available" ? "default" : "destructive"}
                                  className="ml-1 text-[10px]">
                          {r.state}
                        </Badge>
                      </span>
                    ))}
                    {arr.length > 3 && <span> +{arr.length - 3} more</span>}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLineDrawer(null)}
                          data-testid="line-drawer-close">Close</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}

function AdjustmentDialog({ open, onClose, onDone, sid }) {
  const [form, setForm] = useState({
    kind: "credit_adjustment", amountPence: 0, reason: "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/${sid}/adjustments`, form, auth());
      toast.success("Adjustment applied");
      onDone?.();
    } catch (e) { toast.error(humanise(e)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="adjust-dialog">
        <DialogHeader>
          <DialogTitle>Apply adjustment</DialogTitle>
          <DialogDescription>
            Issued statements are never edited. Credit/debit adjustments create
            a new version; correction/replacement supersede the statement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v) => upd("kind", v)}>
              <SelectTrigger data-testid="adjust-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ADJUSTMENT_KINDS.map((k) => (
                  <SelectItem key={k} value={k} data-testid={`adjust-kind-${k}`}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (pence)</Label>
            <Input type="number" value={form.amountPence}
                     onChange={(e) => upd("amountPence", parseInt(e.target.value || "0", 10))}
                     data-testid="adjust-amount" />
          </div>
          <div>
            <Label>Reason</Label>
            <Textarea value={form.reason}
                        onChange={(e) => upd("reason", e.target.value)}
                        data-testid="adjust-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="adjust-cancel">Cancel</Button>
          <Button onClick={submit} disabled={loading || !form.reason.trim()}
                    data-testid="adjust-submit">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Root component ─────────────────────────────────────────────────────
export default function StatementManagementConsole() {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");
  const [genOpen, setGenOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);
  const openStatement = (id) => { setSelected(id); setTab("detail"); };

  const role = (user?.role || "").toLowerCase();
  const canWrite = WRITE_ROLES.has(role);

  useEffect(() => { document.title = "Engage — Statements"; }, []);

  useEffect(() => {
    axios.get(`${API}/health`, auth())
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null));
  }, []);

  if (!user) return (
    <div className="p-6 text-center text-muted-foreground" data-testid="stmt-noauth">
      Sign in required.
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="engage-v2-statements-console">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-3xl font-semibold">Operational Statements</h1>
          <p className="text-sm text-muted-foreground">
            Deterministic engine — every figure reconstructs from immutable
            operational events. Statements-only; no payment execution.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Engage v2</Badge>
          <Badge variant={canWrite ? "default" : "secondary"}
                    data-testid="stmt-role-badge">
            {canWrite ? "write" : "read-only"}
          </Badge>
          <Button onClick={() => setGenOpen(true)} disabled={!canWrite}
                    data-testid="stmt-generate-btn">Generate</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-7 w-full" data-testid="stmt-tabs">
          <TabsTrigger value="overview" data-testid="stmt-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="stmt-tab-drafts">Drafts</TabsTrigger>
          <TabsTrigger value="awaiting" data-testid="stmt-tab-awaiting">Awaiting Approval</TabsTrigger>
          <TabsTrigger value="issued" data-testid="stmt-tab-issued">Issued</TabsTrigger>
          <TabsTrigger value="history" data-testid="stmt-tab-history">History</TabsTrigger>
          <TabsTrigger value="adjustments" data-testid="stmt-tab-adjustments">Adjustments</TabsTrigger>
          <TabsTrigger value="detail" data-testid="stmt-tab-detail">Detail</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab health={health} /></TabsContent>
        <TabsContent value="drafts">
          <StatementList lifecycleState="calculated" refreshKey={refreshKey}
                            onOpen={openStatement} />
        </TabsContent>
        <TabsContent value="awaiting">
          <StatementList lifecycleState="awaiting_approval" refreshKey={refreshKey}
                            onOpen={openStatement} />
        </TabsContent>
        <TabsContent value="issued">
          <StatementList lifecycleState="issued" refreshKey={refreshKey}
                            onOpen={openStatement} />
        </TabsContent>
        <TabsContent value="history">
          <StatementList lifecycleState="" refreshKey={refreshKey}
                            onOpen={openStatement} />
        </TabsContent>
        <TabsContent value="adjustments">
          <StatementList lifecycleState="superseded" refreshKey={refreshKey}
                            onOpen={openStatement} />
        </TabsContent>
        <TabsContent value="detail">
          {selected ? (
            <StatementDetail sid={selected} canWrite={canWrite}
                                onClose={() => setSelected(null)}
                                onRefresh={bump} />
          ) : (
            <div className="text-sm text-muted-foreground" data-testid="stmt-detail-empty">
              Select a statement from any tab to see its details, evidence and lifecycle actions.
            </div>
          )}
        </TabsContent>
      </Tabs>

      <GenerateDialog open={genOpen} onClose={() => setGenOpen(false)}
                          onGenerated={(id) => { openStatement(id); bump(); }}
                          canWrite={canWrite} />
    </div>
  );
}
